<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import ArrowRight from '~/assets/icon/arrow-right.svg'
import DownloadIcon from '~/assets/icon/store-download.svg'
import type { ExchangeSoftwareItem } from '~/types/exchange-software-module'
import { getHomeStoreSoftwareDetailPath, ensureHomeStoreLocalePrefix } from '~/utils/homeStore'

/** 软件标签，接口在不同版本中可能返回 tagName/tagColor 或 name/color。 */
export interface ExchangeSoftwareTag {
  tagName?: string | number | null
  tagColor?: string | null
  name?: string | number | null
  color?: string | null
  [key: string]: unknown
}

/** 服务器 IP 明细。 */
export interface ExchangeServerIp {
  ip?: string | null
  pingText?: string | null
  velocity?: string | number | null
  countryName?: string | null
  flag?: string | null
  [key: string]: unknown
}

/** 服务器条目。 */
export interface ExchangeServerItem {
  serverName?: string | null
  serverTypeText?: string | null
  serverTypeColor?: string | null
  ipCount?: string | number | null
  regulationCountryName?: string | null
  regulationCountrygFlag?: string | null
  leverage?: string | number | null
  leverageDetailList?: unknown
  fastestServerIp?: string | null
  fastestServerPingText?: string | null
  fastestServerVelocity?: string | number | null
  serverIPList?: unknown
  companyBasic?: unknown
  [key: string]: unknown
}

/** MT4/5 鉴定结果。 */
export interface ExchangeSoftwareAppraise {
  identificationResult?: {
    licenses?: unknown
    mT4_Count?: string | number | null
    mT5_Count?: string | number | null
    averageVelocityText?: string | number | null
    velocityIcon?: string | null
    [key: string]: unknown
  } | null
  serverList?: {
    allServers?: unknown
    mT4_Servers?: unknown
    mT5_Servers?: unknown
    [key: string]: unknown
  } | null
  [key: string]: unknown
}

/** API 层聚合后的软件数据。所有字段均可选，便于接口降级时安全渲染。 */
export interface ExchangeSoftwarePayload {
  mt4mt5SoftwareList?: ExchangeSoftwareItem[]
  selfSoftwareList?: ExchangeSoftwareItem[]
  iosSelfSoftwareList?: ExchangeSoftwareItem[]
  androidSelfSoftwareList?: ExchangeSoftwareItem[]
  ctraderSoftwareList?: ExchangeSoftwareItem[]
  iosCtraderSoftwareList?: ExchangeSoftwareItem[]
  androidCtraderSoftwareList?: ExchangeSoftwareItem[]
  mt4mt5AppraiseData?: ExchangeSoftwareAppraise | null
  [key: string]: unknown
}

const props = withDefaults(
  defineProps<{
    softwareData?: ExchangeSoftwarePayload | null
    traderCode?: string | number | null
    exchangeName?: string | null
    /** 可由页面覆盖默认 Store 详情跳转行为。 */
    onSoftwareDetail?: (software: ExchangeSoftwareItem) => void | Promise<void>
  }>(),
  {
    softwareData: null,
    traderCode: '',
    exchangeName: '',
    onSoftwareDetail: undefined,
  },
)

const { locale, t } = useAppI18n()

const isIpDialogOpen = ref(false)
const isLeverageDialogOpen = ref(false)
const isAppraiseDialogOpen = ref(false)
const isSoftwareDialogOpen = ref(false)
const selectedServer = ref<ExchangeServerItem | null>(null)
const selectedLicense = ref<Record<string, unknown> | null>(null)
const previewVisible = ref(false)
const previewImages = ref<string[]>([])
const previewInitialIndex = ref(0)
const activePlatform = ref<'all' | 'ios' | 'android'>('all')
const activeServerTab = ref<'all' | 'mt4' | 'mt5'>('all')

// 拖拽截图时记录起点；移动超过阈值后抑制随后的 click，避免误打开详情。
const dragState = reactive({
  active: false,
  moved: false,
  startX: 0,
  startScrollLeft: 0,
  target: null as HTMLElement | null,
  captureTarget: null as HTMLElement | null,
})

const normalizeText = (value: unknown, fallback = '') => {
  if (value === null || value === undefined) return fallback
  const text = String(value).trim()
  return text || fallback
}

const toRecord = (value: unknown): Record<string, unknown> => {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

const toArray = <T = Record<string, unknown>>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[]
  const record = toRecord(value)
  if (Array.isArray(record.items)) return record.items as T[]
  if (Array.isArray(record.list)) return record.list as T[]
  if (Array.isArray(record.result)) return record.result as T[]
  return []
}

const translate = (key: string, fallback: string) => {
  const result = t(key)
  return result === key ? fallback : result
}

const payload = computed(() => props.softwareData || {})
const appraise = computed(() => toRecord(payload.value.mt4mt5AppraiseData))
const appraiseIdentification = computed(() => toRecord(appraise.value.identificationResult))
const appraiseServerList = computed(() => toRecord(appraise.value.serverList))

const mtSoftwareList = computed<ExchangeSoftwareItem[]>(() =>
  toArray<ExchangeSoftwareItem>(payload.value.mt4mt5SoftwareList),
)
const selfSoftwareList = computed<ExchangeSoftwareItem[]>(() =>
  toArray<ExchangeSoftwareItem>(payload.value.selfSoftwareList),
)
const iosSelfSoftwareList = computed<ExchangeSoftwareItem[]>(() =>
  toArray<ExchangeSoftwareItem>(payload.value.iosSelfSoftwareList),
)
const androidSelfSoftwareList = computed<ExchangeSoftwareItem[]>(() =>
  toArray<ExchangeSoftwareItem>(payload.value.androidSelfSoftwareList),
)
const ctraderSoftwareList = computed<ExchangeSoftwareItem[]>(() =>
  toArray<ExchangeSoftwareItem>(payload.value.ctraderSoftwareList),
)
const iosCtraderSoftwareList = computed<ExchangeSoftwareItem[]>(() =>
  toArray<ExchangeSoftwareItem>(payload.value.iosCtraderSoftwareList),
)
const androidCtraderSoftwareList = computed<ExchangeSoftwareItem[]>(() =>
  toArray<ExchangeSoftwareItem>(payload.value.androidCtraderSoftwareList),
)

const licenses = computed<Record<string, unknown>[]>(() =>
  toArray<Record<string, unknown>>(appraiseIdentification.value.licenses),
)
const serverLists = computed(() => ({
  all: toArray<ExchangeServerItem>(appraiseServerList.value.allServers),
  mt4: toArray<ExchangeServerItem>(appraiseServerList.value.mT4_Servers),
  mt5: toArray<ExchangeServerItem>(appraiseServerList.value.mT5_Servers),
}))

const serverTabs = computed(() => {
  const tabs: Array<{ key: 'all' | 'mt4' | 'mt5'; label: string; count: number }> = []
  if (serverLists.value.all.length) {
    tabs.push({ key: 'all', label: translate('全部服务器', '全部服务器'), count: serverLists.value.all.length })
  }
  if (serverLists.value.mt4.length) tabs.push({ key: 'mt4', label: 'MT4', count: serverLists.value.mt4.length })
  if (serverLists.value.mt5.length) tabs.push({ key: 'mt5', label: 'MT5', count: serverLists.value.mt5.length })
  return tabs
})

const currentServers = computed(() => serverLists.value[activeServerTab.value] || [])
const hasMtSection = computed(
  () =>
    mtSoftwareList.value.length > 0 ||
    licenses.value.length > 0 ||
    serverTabs.value.length > 0 ||
    appraiseIdentification.value.mT4_Count !== undefined ||
    appraiseIdentification.value.mT5_Count !== undefined,
)

type PlatformTab = { key: 'all' | 'ios' | 'android'; label: string; items: ExchangeSoftwareItem[] }

const buildPlatformTabs = (
  all: ExchangeSoftwareItem[],
  ios: ExchangeSoftwareItem[],
  android: ExchangeSoftwareItem[],
): PlatformTab[] => {
  const tabs: PlatformTab[] = []
  if (all.length || (ios.length && android.length)) {
    const merged = all.length ? all : [...ios, ...android]
    tabs.push({ key: 'all', label: translate('73084', '全部'), items: merged })
  }
  if (ios.length) tabs.push({ key: 'ios', label: 'iOS', items: ios })
  if (android.length) tabs.push({ key: 'android', label: 'Android', items: android })
  return tabs
}

/** 合并自研与 cTrader 列表，去掉同一软件在聚合接口中的重复记录。 */
const dedupeSoftwareItems = (items: ExchangeSoftwareItem[]) => {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = normalizeText(
      item.softwareId || `${item.appBundleId || ''}:${item.marketId || ''}:${item.name || ''}:${item.logo || ''}`,
    )
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const modernSoftwareLists = computed(() => ({
  all: dedupeSoftwareItems([
    ...selfSoftwareList.value,
    ...ctraderSoftwareList.value,
    ...iosSelfSoftwareList.value,
    ...androidSelfSoftwareList.value,
    ...iosCtraderSoftwareList.value,
    ...androidCtraderSoftwareList.value,
  ]),
  ios: dedupeSoftwareItems([...iosSelfSoftwareList.value, ...iosCtraderSoftwareList.value]),
  android: dedupeSoftwareItems([...androidSelfSoftwareList.value, ...androidCtraderSoftwareList.value]),
}))

const modernPlatformTabs = computed(() =>
  buildPlatformTabs(modernSoftwareLists.value.all, modernSoftwareLists.value.ios, modernSoftwareLists.value.android),
)

// 每个平台 tab 只展示前三条，tab 上的数量仍保留接口返回的完整数量。
const MAX_MODERN_ITEMS_PER_TAB = 3
const modernItems = computed(() => {
  const activeTab = modernPlatformTabs.value.find((tab) => tab.key === activePlatform.value)
  return activeTab?.items || modernPlatformTabs.value[0]?.items || []
})
const visibleModernItems = computed(() => modernItems.value.slice(0, MAX_MODERN_ITEMS_PER_TAB))

const hasModernSection = computed(() => modernPlatformTabs.value.some((tab) => tab.items.length > 0))
const hasContent = computed(() => hasMtSection.value || hasModernSection.value)

// 异步数据切换后，若“全部服务器”不存在则自动选中第一个可用分类。
watch(
  serverTabs,
  (tabs) => {
    if (!tabs.some((tab) => tab.key === activeServerTab.value)) {
      activeServerTab.value = tabs[0]?.key || 'all'
    }
  },
  { immediate: true },
)

// 数据异步返回后，若当前平台不存在则回到第一个可用分类。
watch(
  modernPlatformTabs,
  (tabs) => {
    if (!tabs.some((tab) => tab.key === activePlatform.value)) {
      activePlatform.value = tabs[0]?.key || 'all'
    }
  },
  { immediate: true },
)

const sectionTitle = computed(() =>
  hasModernSection.value ? translate('63958', '交易应用') : translate('相关软件', '相关软件'),
)
// const mtTitle = computed(() => translate('MT4/5', 'MT4/5'))
// const mt4CountLabel = computed(() => translate('49235', 'MT4数量'))
// const mt5CountLabel = computed(() => translate('49236', 'MT5数量'))
// const velocityLabel = computed(() => `${translate('49237', '平均响应速度')}/ms`)
// const countryLabel = computed(() => translate('36975', '监管国家'))
// const leverageLabel = computed(() => translate('53966', '杠杆'))
// const ipLabel = computed(() => translate('21441', '服务器 IP'))
// const totalDownloadsLabel = computed(() => translate('64089', '下载量'))
// const softwareSizeLabel = computed(() => translate('63967', '软件大小'))
// const relatedTraderLabel = computed(() => translate('关联交易商', '关联交易商'))

const normalizeImageList = (value: unknown) => {
  const values = Array.isArray(value) ? value : toArray(value)
  return values
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      const record = toRecord(item)
      return normalizeText(record.url || record.src || record.image || record.webImage)
    })
    .filter(Boolean)
}

const getSoftwareTags = (item: ExchangeSoftwareItem) => {
  const raw = item.identificationResult
  const list = Array.isArray(raw) ? raw : toArray(raw)
  return list
    .map((tag) => {
      const record = toRecord(tag)
      const name = normalizeText(record.tagName || record.name || record.content)
      const color = normalizeText(record.tagColor || record.color, '#1f845a')
      return name ? { name, color } : null
    })
    .filter((tag): tag is { name: string; color: string } => Boolean(tag))
}

const getSystemLabels = (item: ExchangeSoftwareItem) => {
  const values = normalizeImageList(item.systemTypeImg)
  return values.length ? values : [Number(item.marketId) === 1 ? 'iOS' : 'Android']
}

/** 单条通栏卡片显示当前交易商，接口有专属名称时优先使用接口值。 */
const getRelatedTraderName = (item: ExchangeSoftwareItem) =>
  normalizeText(item.traderName || item.exchangeName || props.exchangeName || item.traderCode, '--')

/** 关联交易商图标字段在新旧接口中名称不一致，按优先级兼容。 */
const getRelatedTraderIcon = (item: ExchangeSoftwareItem) =>
  normalizeText(item.companyIcon || item.traderIcon || item.exchangeIcon)

const formatNumber = (value: unknown) => {
  const text = normalizeText(value)
  if (!text) return '--'
  const numeric = Number(text)
  if (!Number.isFinite(numeric)) return text
  return new Intl.NumberFormat(locale).format(numeric)
}

const getPingIcon = (velocity: unknown) => {
  const code = Number(velocity)
  if (code === 1) return 'https://resources1.interface003.com/web20/img/commen/green.gif'
  if (code === 2) return 'https://resources1.interface003.com/web20/img/commen/yellow.gif'
  if (code === 3) return 'https://resources1.interface003.com/web20/img/commen/red.gif'
  return ''
}

const getServerIps = (server: ExchangeServerItem) => {
  const source = toRecord(server.serverIPList)
  return {
    title: normalizeText(source.title || server.serverName, translate('服务器 IP', '服务器 IP')),
    updateDate: normalizeText(source.updateDate),
    list: toArray<ExchangeServerIp>(source.list || source.items),
  }
}

const getLeverages = (server: ExchangeServerItem) => toArray<Record<string, unknown>>(server.leverageDetailList)

const switchServerTab = (key: 'all' | 'mt4' | 'mt5') => {
  activeServerTab.value = key
}

const openServerIps = (server: ExchangeServerItem) => {
  if (!getServerIps(server).list.length) return
  selectedServer.value = server
  isIpDialogOpen.value = true
}

const openLeverage = (server: ExchangeServerItem) => {
  if (!getLeverages(server).length) return
  selectedServer.value = server
  isLeverageDialogOpen.value = true
}

const openLicense = (license: Record<string, unknown>) => {
  selectedLicense.value = license
  isAppraiseDialogOpen.value = true
}

const openPreview = (item: ExchangeSoftwareItem, index: number) => {
  const images = normalizeImageList(item.screenshots)
  if (!images.length) return
  previewImages.value = images
  previewInitialIndex.value = Math.max(0, Math.min(index, images.length - 1))
  previewVisible.value = true
}

const openSoftwareDetail = (item: ExchangeSoftwareItem) => {
  if (props.onSoftwareDetail) {
    void props.onSoftwareDetail(item)
    return
  }

  const detailPath = getHomeStoreSoftwareDetailPath({
    ...item,
    traderCode: item.traderCode || props.traderCode,
  })
  if (!detailPath || detailPath === '/') return

  const localizedPath = ensureHomeStoreLocalePrefix(detailPath, locale)
  const externalUrl = new URL(localizedPath, 'https://store.wikibit.com').toString()
  void navigateTo(externalUrl, { external: true, open: { target: '_blank' } })
}

/** 打开完整交易应用列表，供模块头部的数量入口使用。 */
const openSoftwareList = () => {
  if (!modernSoftwareLists.value.all.length) return
  isSoftwareDialogOpen.value = true
}

const startGalleryDrag = (event: PointerEvent) => {
  const target = event.currentTarget as HTMLElement | null
  if (!target) return
  dragState.active = true
  dragState.moved = false
  dragState.startX = event.clientX
  dragState.startScrollLeft = target.scrollLeft
  dragState.target = target
  dragState.captureTarget = event.target instanceof HTMLElement ? event.target : target
  dragState.captureTarget.setPointerCapture?.(event.pointerId)
}

const moveGalleryDrag = (event: PointerEvent) => {
  if (!dragState.active || !dragState.target) return
  const distance = event.clientX - dragState.startX
  if (Math.abs(distance) > 4) dragState.moved = true
  dragState.target.scrollLeft = dragState.startScrollLeft - distance
}

const stopGalleryDrag = (event?: PointerEvent) => {
  if (dragState.captureTarget && event?.pointerId !== undefined) {
    dragState.captureTarget.releasePointerCapture?.(event.pointerId)
  }
  dragState.active = false
  dragState.target = null
  dragState.captureTarget = null
  if (dragState.moved) {
    window.setTimeout(() => {
      dragState.moved = false
    }, 0)
  }
}

const shouldOpenPreview = (item: ExchangeSoftwareItem, index: number, event: MouseEvent) => {
  if (dragState.moved) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  openPreview(item, index)
}
</script>

<template>
  <section
    v-if="hasContent"
    id="exchange-software-tab"
    class="exchange-software-module mt-[40px] flex w-full flex-col rounded-[12px] border border-solid border-[rgba(11,18,40,0.14)] bg-[#fff] p-[24px] text-[#292a2e] sm:p-[20px]"
    aria-labelledby="exchange-software-title"
  >
    <header
      class="flex w-full items-center justify-between gap-[8px] border-b-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pb-[16px]"
    >
      <h2 id="exchange-software-title" class="m-0 text-[20px] leading-[28px] font-[600]">{{ sectionTitle }}</h2>
      <div
        v-if="hasModernSection"
        class="flex shrink-0 cursor-pointer items-center gap-[4px] rounded-[4px] transition hover:bg-[#f8f8f8]"
        role="button"
        tabindex="0"
        @click="openSoftwareList"
        @keydown.enter="openSoftwareList"
        @keydown.space.prevent="openSoftwareList"
      >
        <span
          class="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#f8f8f8] px-[4px] text-[14px] leading-[18px] text-[#7d818a]"
        >
          {{ modernSoftwareLists.all.length }}
        </span>
        <ArrowRight class="size-[20px] rtl:rotate-180" aria-hidden="true" />
      </div>
    </header>

    <section v-if="hasModernSection" class="flex w-full flex-col" aria-labelledby="exchange-software-title">
      <div class="flex w-full gap-[8px] overflow-x-auto py-[16px]" role="tablist" aria-label="交易应用平台">
        <button
          v-for="tab in modernPlatformTabs"
          :key="tab.key"
          type="button"
          role="tab"
          :aria-selected="activePlatform === tab.key"
          class="shrink-0 cursor-pointer rounded-[2px] border-[0.5px] border-solid px-[12px] py-[4px] text-[13px] leading-[20px] whitespace-nowrap transition"
          :class="
            activePlatform === tab.key
              ? 'border-[#112d73] bg-[#112d73] font-[500] text-[#fff] hover:bg-[#112d73]'
              : 'border-[rgba(11,18,40,0.14)] bg-[#fff] font-[400] text-[#292a2e] hover:bg-[#f8f8f8]'
          "
          @click="activePlatform = tab.key"
        >
          {{ tab.label === 'iOS' ? 'IOS' : tab.label }} {{ tab.items.length }}
        </button>
      </div>

      <div v-if="visibleModernItems.length === 1" class="flex w-full">
        <article
          v-for="(software, softwareIndex) in visibleModernItems"
          :key="`${normalizeText(software.softwareId)}-${softwareIndex}`"
          class="flex min-h-[212px] w-full min-w-0 cursor-pointer items-stretch gap-[30px] rounded-[8px] bg-[#fafafa] p-[26px] transition hover:bg-[#f2f3f5] max-[1024px]:flex-col max-[1024px]:gap-[24px] max-[1024px]:p-[20px]"
          @click="openSoftwareDetail(software)"
        >
          <div class="flex min-w-0 flex-1 flex-col justify-between gap-[34px]">
            <div class="flex min-w-0 items-center gap-[10px]">
              <CommonImage
                :src="normalizeText(software.logo)"
                :alt="normalizeText(software.name, 'software')"
                width="48"
                height="48"
                class="size-[48px] shrink-0 rounded-[4px] object-cover"
                loading="lazy"
              />
              <div class="flex min-w-0 flex-col gap-[2px]">
                <div class="truncate text-[18px] leading-[24px] font-[500]">
                  {{ normalizeText(software.name, '--') }}
                </div>
                <div class="truncate text-[14px] leading-[22px] text-[#86909c]">
                  {{ normalizeText(software.title || software.developerName, '--') }}
                </div>
              </div>
            </div>

            <div class="flex min-w-0 items-center gap-[40px] max-[640px]:flex-wrap max-[640px]:gap-[20px]">
              <div class="flex min-w-0 flex-col gap-[10px]">
                <span class="text-[14px] leading-[22px] text-[#86909c]">{{ t('下载量') }}</span>
                <strong class="truncate text-[16px] leading-[24px] font-[500] text-[#3d3d3d]">
                  {{ formatNumber(software.downloadNum) }}
                </strong>
              </div>
              <span class="h-[52px] w-[0.5px] shrink-0 bg-[#e5e6eb]" aria-hidden="true"></span>
              <div class="flex min-w-0 flex-col gap-[10px]">
                <span class="text-[14px] leading-[22px] text-[#86909c]">{{ t('软件大小') }}</span>
                <strong class="truncate text-[16px] leading-[24px] font-[500] text-[#3d3d3d]">
                  {{ normalizeText(software.sizes, '--') }}
                </strong>
              </div>
              <span class="h-[52px] w-[0.5px] shrink-0 bg-[#e5e6eb]" aria-hidden="true"></span>
              <div class="flex min-w-0 flex-col gap-[10px]">
                <span class="text-[14px] leading-[22px] text-[#86909c]">{{ t('关联交易所') }}</span>
                <span class="flex min-w-0 items-center gap-[6px]">
                  <CommonImage
                    v-if="getRelatedTraderIcon(software)"
                    :src="getRelatedTraderIcon(software)"
                    :alt="getRelatedTraderName(software)"
                    width="24"
                    height="24"
                    class="size-[24px] shrink-0 rounded-[4px] object-cover"
                    loading="lazy"
                  />
                  <span class="truncate text-[16px] leading-[24px] font-[500] text-[#3d3d3d]">
                    {{ getRelatedTraderName(software) }}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="normalizeImageList(software.screenshots).length"
            class="screenshot-scrollbar flex h-[168px] w-[474px] shrink-0 cursor-grab items-start gap-[10px] overflow-x-auto overflow-y-hidden overscroll-x-contain active:cursor-grabbing max-[1024px]:w-full"
            @pointerdown.stop="startGalleryDrag"
            @pointermove.stop="moveGalleryDrag"
            @pointerup.stop="stopGalleryDrag"
            @pointercancel.stop="stopGalleryDrag"
            @pointerleave.stop="stopGalleryDrag"
            @click.stop
          >
            <button
              v-for="(image, imageIndex) in normalizeImageList(software.screenshots)"
              :key="`${image}-${imageIndex}`"
              type="button"
              class="block h-[160px] w-[74px] min-w-[74px] shrink-0 cursor-pointer overflow-hidden rounded-[8px] transition hover:opacity-85"
              @click.stop.prevent="shouldOpenPreview(software, imageIndex, $event)"
            >
              <CommonImage
                :src="image"
                :alt="`${normalizeText(software.name, 'software')} screenshot ${imageIndex + 1}`"
                width="74"
                height="160"
                class="size-full object-cover"
                loading="lazy"
              />
            </button>
          </div>
        </article>
      </div>

      <div
        v-else
        class="grid w-full gap-[24px] max-[1024px]:grid-cols-2 max-[640px]:grid-cols-1"
        :class="visibleModernItems.length === 2 ? 'grid-cols-2' : 'grid-cols-3'"
      >
        <article
          v-for="(software, softwareIndex) in visibleModernItems"
          :key="`${normalizeText(software.softwareId)}-${softwareIndex}`"
          class="-mx-[8px] flex min-w-0 cursor-pointer flex-col gap-[12px] rounded-[8px] bg-[#fff] p-[8px] transition-[box-shadow] duration-[200ms] hover:shadow-[0_12px_32px_rgba(8,19,62,0.08)]"
          @click="openSoftwareDetail(software)"
        >
          <div
            class="flex min-w-0 items-center gap-[12px] border-b-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pb-[12px]"
          >
            <div class="relative size-[38px] shrink-0 overflow-hidden rounded-[8px]">
              <CommonImage
                :src="normalizeText(software.logo)"
                :alt="normalizeText(software.name, 'software')"
                width="40"
                height="40"
                class="size-full border border-solid border-[#ededed] object-cover"
                loading="lazy"
              />
              <span
                class="absolute top-0 right-0 z-[2] flex size-[14px] items-center justify-center rounded-tr-[7px] rounded-bl-[7px] bg-[rgba(0,0,0,0.4)] rtl:right-auto rtl:left-0 rtl:rounded-tl-[7px] rtl:rounded-tr-none rtl:rounded-br-[7px] rtl:rounded-bl-none"
              >
                <CommonImage
                  v-if="Number(software.marketId) === 1"
                  src="https://resources1.interface003.com/web30/2026/09/16/714da195-1b95-4a1e-a13b-be29ee4c7347.png"
                  width="10"
                  height="10"
                  loading="eager"
                />
                <CommonImage
                  v-else
                  src="https://resources1.interface003.com/web30/2026/09/16/c97c0119-376e-4e44-b55c-e7c1029705cd.png"
                  width="10"
                  height="10"
                  loading="eager"
                />
              </span>
            </div>
            <div class="flex min-w-0 flex-1 flex-col gap-[2px]">
              <div class="truncate text-[16px] leading-[24px] font-[500]">
                {{ normalizeText(software.name, '--') }}
              </div>
              <div class="truncate text-[12px] leading-[18px] text-[#7d818a]">
                {{ normalizeText(software.title || software.developerName, '--') }}
              </div>
            </div>
            <div class="flex shrink-0 flex-col items-end gap-[2px] text-[16px] leading-[24px] font-[500]">
              <DownloadIcon class="size-[16px]" aria-hidden="true" />
              <span>{{ formatNumber(software.downloadNum) }}</span>
            </div>
          </div>

          <div
            v-if="normalizeImageList(software.screenshots).length"
            class="screenshot-scrollbar flex h-[150px] cursor-grab items-start gap-[10px] overflow-x-auto overflow-y-hidden overscroll-x-contain active:cursor-grabbing"
            @pointerdown.stop="startGalleryDrag"
            @pointermove.stop="moveGalleryDrag"
            @pointerup.stop="stopGalleryDrag"
            @pointercancel.stop="stopGalleryDrag"
            @pointerleave.stop="stopGalleryDrag"
            @click.stop
          >
            <button
              v-for="(image, imageIndex) in normalizeImageList(software.screenshots)"
              :key="`${image}-${imageIndex}`"
              type="button"
              class="block h-[142px] shrink-0 cursor-pointer overflow-hidden rounded-[4px] transition hover:opacity-85"
              :style="{ flexBasis: 'calc((100% - 40px) / 5)' }"
              @click.stop.prevent="shouldOpenPreview(software, imageIndex, $event)"
            >
              <CommonImage
                :src="image"
                :alt="`${normalizeText(software.name, 'software')} screenshot ${imageIndex + 1}`"
                width="100%"
                height="142"
                class="size-full object-cover"
                loading="lazy"
              />
            </button>
          </div>
        </article>
      </div>
    </section>

    <section
      v-if="hasMtSection"
      class="mt-[32px] flex w-full flex-col gap-[20px]"
      aria-labelledby="exchange-software-mt-title"
    >
      <h3
        id="exchange-software-mt-title"
        class="m-0 text-[24px] leading-[32px] font-[600] text-[#292929] sm:text-[20px]"
      >
        MT4/5
      </h3>

      <div
        v-if="
          licenses.length ||
          appraiseIdentification.mT4_Count !== undefined ||
          appraiseIdentification.mT5_Count !== undefined ||
          appraiseIdentification.averageVelocityText !== undefined
        "
        class="flex flex-col gap-[20px]"
      >
        <div class="flex flex-wrap items-center gap-[12px]">
          <button
            v-for="(license, index) in licenses"
            :key="`${normalizeText(license.status)}-${index}`"
            type="button"
            class="flex min-h-[64px] min-w-[180px] cursor-pointer items-center justify-between gap-[12px] rounded-[8px] px-[16px] py-[10px] text-start transition hover:brightness-95"
            :class="
              Number(license.status) === 11
                ? 'bg-[linear-gradient(262deg,rgba(4,161,135,0.04),rgba(4,161,135,0.12))]'
                : 'bg-[linear-gradient(263deg,rgba(230,155,11,0.04),rgba(230,155,11,0.12))]'
            "
            @click="openLicense(license)"
          >
            <span class="flex min-w-0 items-center gap-[10px]">
              <CommonImage
                :src="normalizeText(license.icon)"
                :alt="normalizeText(license.content, 'license')"
                width="40"
                height="40"
                class="size-[40px] shrink-0 object-contain"
              />
              <span
                class="truncate text-[18px] leading-[24px] font-[600]"
                :style="{ color: normalizeText(license.color, '#1f845a') }"
              >
                {{ normalizeText(license.content, '鉴定结果') }}
              </span>
            </span>
            <span class="text-[20px]" aria-hidden="true">›</span>
          </button>

          <div class="grid min-w-[320px] flex-1 grid-cols-3 gap-[12px] sm:min-w-0">
            <div
              class="flex min-h-[64px] flex-col items-center justify-center rounded-[8px] bg-[#fafafa] px-[8px] py-[8px]"
            >
              <strong class="text-[20px] leading-[28px] font-[700]">{{
                normalizeText(appraiseIdentification.mT4_Count, '--')
              }}</strong>
              <span class="text-[12px] leading-[18px] text-[#4e5969]">{{ t('MT4数量') }}</span>
            </div>
            <div
              class="flex min-h-[64px] flex-col items-center justify-center rounded-[8px] bg-[#fafafa] px-[8px] py-[8px]"
            >
              <strong class="text-[20px] leading-[28px] font-[700]">{{
                normalizeText(appraiseIdentification.mT5_Count, '--')
              }}</strong>
              <span class="text-[12px] leading-[18px] text-[#4e5969]">{{ t('MT5数量') }}</span>
            </div>
            <div
              class="flex min-h-[64px] flex-col items-center justify-center rounded-[8px] bg-[#fafafa] px-[8px] py-[8px]"
            >
              <strong class="flex items-center gap-[4px] text-[20px] leading-[28px] font-[700]">
                {{ normalizeText(appraiseIdentification.averageVelocityText, '--') }}
                <CommonImage
                  v-if="normalizeText(appraiseIdentification.velocityIcon)"
                  :src="normalizeText(appraiseIdentification.velocityIcon)"
                  alt=""
                  width="16"
                  height="20"
                  class="size-[16px] object-contain"
                />
              </strong>
              <span class="text-[12px] leading-[18px] text-[#4e5969]">{{ t('平均响应速度') }}/ms</span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="mtSoftwareList.length"
        class="flex w-full gap-[12px] overflow-x-auto pb-[4px]"
        style="scrollbar-width: none"
      >
        <article
          v-for="(software, index) in mtSoftwareList"
          :key="`${normalizeText(software.softwareId)}-${index}`"
          class="flex min-h-[162px] w-[calc(25%-9px)] min-w-[180px] shrink-0 cursor-pointer flex-col items-center justify-center gap-[8px] rounded-[8px] bg-[#fafafa] p-[16px] transition hover:bg-[#f2f3f5] sm:w-[calc(50%-6px)] lg:w-[calc(33.33%-8px)]"
          @click="openSoftwareDetail(software)"
        >
          <CommonImage
            :src="normalizeText(software.logo)"
            :alt="normalizeText(software.name)"
            width="38"
            height="38"
            class="size-[38px] object-contain"
            loading="lazy"
          />
          <div class="line-clamp-1 max-w-full text-[16px] leading-[22px] font-[600]">
            {{ normalizeText(software.name, '--') }}
          </div>
          <div v-if="getSoftwareTags(software).length" class="flex max-w-full flex-wrap justify-center gap-[6px]">
            <span
              v-for="tag in getSoftwareTags(software)"
              :key="`${tag.name}-${tag.color}`"
              class="max-w-full truncate rounded-[4px] border-[0.5px] border-solid px-[6px] text-[12px] leading-[18px]"
              :style="{ color: tag.color, borderColor: tag.color }"
              >{{ tag.name }}</span
            >
          </div>
          <div class="flex min-h-[18px] items-center gap-[6px]">
            <template v-for="(system, systemIndex) in getSystemLabels(software)" :key="`${system}-${systemIndex}`">
              <span
                v-if="system === 'iOS' || system === 'Android'"
                class="rounded-[3px] bg-[#e5e6eb] px-[4px] text-[10px] leading-[16px] text-[#4e5969]"
                >{{ system }}</span
              >
              <CommonImage v-else :src="system" alt="" width="18" height="18" class="size-[18px] object-contain" />
            </template>
          </div>
        </article>
      </div>
    </section>

    <section
      v-if="serverTabs.length"
      class="flex w-full flex-col gap-[16px]"
      aria-labelledby="exchange-software-server-title"
    >
      <h3 id="exchange-software-server-title" class="m-0 text-[20px] leading-[28px] font-[600]">
        {{ translate('服务器', '服务器') }}
      </h3>
      <div class="flex gap-[8px] overflow-x-auto pb-[2px]" role="tablist" aria-label="服务器类型">
        <button
          v-for="tab in serverTabs"
          :key="tab.key"
          type="button"
          role="tab"
          :aria-selected="activeServerTab === tab.key"
          class="cursor-pointer rounded-[2px] px-[16px] py-[5px] text-[13px] leading-[18px] whitespace-nowrap transition hover:brightness-95"
          :class="activeServerTab === tab.key ? 'bg-[#1d2129] text-[#fff]' : 'bg-[#f2f3f5] text-[#1d2129]'"
          @click="switchServerTab(tab.key)"
        >
          {{ tab.label }} <span v-if="tab.key === 'all'">{{ tab.count }}</span>
        </button>
      </div>

      <div class="flex w-full gap-[12px] overflow-x-auto pb-[4px]" style="scrollbar-width: none">
        <article
          v-for="(server, index) in currentServers"
          :key="`${normalizeText(server.serverName)}-${index}`"
          class="flex w-[calc(33.33%-8px)] min-w-[286px] shrink-0 flex-col gap-[14px] rounded-[8px] bg-[#fafafa] p-[20px] sm:min-w-[280px]"
        >
          <div class="flex min-h-[44px] items-center justify-between gap-[10px]">
            <div class="flex min-w-0 items-center gap-[8px]">
              <span
                class="shrink-0 rounded-[3px] px-[8px] py-[2px] text-[12px] leading-[20px] text-[#fff]"
                :style="{ backgroundColor: normalizeText(server.serverTypeColor, '#1f3f93') }"
                >{{ normalizeText(server.serverTypeText, 'Server') }}</span
              >
              <span class="truncate text-[16px] leading-[22px] font-[600]">{{
                normalizeText(server.serverName, '--')
              }}</span>
            </div>
            <button
              type="button"
              class="flex shrink-0 cursor-pointer items-center gap-[4px] text-[14px] font-[500] hover:text-[#164b71]"
              @click="openServerIps(server)"
            >
              <span>{{ normalizeText(server.ipCount, '0') }}</span
              ><span aria-hidden="true">›</span>
            </button>
          </div>

          <div class="grid grid-cols-2 gap-[12px]">
            <div class="min-w-0">
              <div class="text-[12px] leading-[18px] text-[#86909c]">{{ t('监管国家') }}</div>
              <div class="mt-[6px] flex min-w-0 items-center gap-[6px]">
                <CommonImage
                  v-if="normalizeText(server.regulationCountrygFlag)"
                  :src="normalizeText(server.regulationCountrygFlag)"
                  :alt="normalizeText(server.regulationCountryName)"
                  width="24"
                  height="16"
                  class="size-[24px] shrink-0 rounded-[2px] object-cover"
                />
                <span class="truncate text-[14px] leading-[20px]">{{
                  normalizeText(server.regulationCountryName, '--')
                }}</span>
              </div>
            </div>
            <button
              type="button"
              class="min-w-0 cursor-pointer text-start hover:text-[#164b71]"
              :disabled="!getLeverages(server).length"
              @click="openLeverage(server)"
            >
              <div class="text-[12px] leading-[18px] text-[#86909c]">{{ t('杠杆') }}</div>
              <div class="mt-[6px] flex items-center gap-[4px] text-[14px] leading-[20px]">
                {{ normalizeText(server.leverage, '--')
                }}<span v-if="getLeverages(server).length" aria-hidden="true">›</span>
              </div>
            </button>
          </div>

          <div class="grid grid-cols-2 gap-[10px] rounded-[4px] bg-[#fff] px-[14px] py-[12px]">
            <div class="min-w-0">
              <div class="text-[12px] leading-[20px] text-[#86909c]">{{ t('服务器 IP') }}</div>
              <div class="mt-[4px] truncate text-[14px] leading-[20px]" dir="ltr">
                {{ normalizeText(server.fastestServerIp, '--') }}
              </div>
            </div>
            <div class="min-w-0">
              <div class="text-[12px] leading-[20px] text-[#86909c]">Ping</div>
              <div class="mt-[4px] flex items-center gap-[4px] text-[14px] leading-[20px]" dir="ltr">
                {{ normalizeText(server.fastestServerPingText, '--') }}
                <CommonImage
                  v-if="getPingIcon(server.fastestServerVelocity)"
                  :src="getPingIcon(server.fastestServerVelocity)"
                  alt=""
                  width="16"
                  height="16"
                  class="size-[16px] object-contain"
                />
              </div>
            </div>
          </div>

          <div
            v-if="normalizeText(toRecord(server.companyBasic).fullName || toRecord(server.companyBasic).shortName)"
            class="truncate border-t-[0.5px] border-dashed border-[rgba(5,21,36,0.08)] pt-[10px] text-[13px] text-[#4e5969]"
          >
            {{ normalizeText(toRecord(server.companyBasic).fullName || toRecord(server.companyBasic).shortName) }}
          </div>
        </article>
      </div>
    </section>

    <ElDialog
      v-model="isIpDialogOpen"
      :title="getServerIps(selectedServer || {}).title"
      width="min(754px, calc(100vw - 32px))"
      append-to-body
    >
      <div v-if="selectedServer" class="flex flex-col gap-[16px]">
        <div
          v-if="
            normalizeText(
              toRecord(selectedServer.companyBasic).fullName || toRecord(selectedServer.companyBasic).shortName,
            )
          "
          class="text-[14px] text-[#4e5969]"
        >
          {{
            normalizeText(
              toRecord(selectedServer.companyBasic).fullName || toRecord(selectedServer.companyBasic).shortName,
            )
          }}
        </div>
        <div class="max-h-[420px] overflow-auto rounded-[8px] border border-solid border-[#e5e6eb]">
          <table class="w-full min-w-[480px] border-collapse text-start text-[13px]">
            <thead class="sticky top-0 bg-[#1d2129] text-[#fff]">
              <tr>
                <th class="px-[12px] py-[10px] text-start">{{ t('服务器 IP') }}</th>
                <th class="px-[12px] py-[10px] text-start">Ping</th>
                <th class="px-[12px] py-[10px] text-start">{{ t('监管国家') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(ip, index) in getServerIps(selectedServer).list"
                :key="`${normalizeText(ip.ip)}-${index}`"
                class="border-b border-solid border-[#f0f2f5] last:border-0 even:bg-[#fafafa]"
              >
                <td class="px-[12px] py-[10px]" dir="ltr">{{ normalizeText(ip.ip, '--') }}</td>
                <td class="px-[12px] py-[10px]" dir="ltr">
                  <span class="inline-flex items-center gap-[4px]"
                    >{{ normalizeText(ip.pingText, '--')
                    }}<CommonImage
                      v-if="getPingIcon(ip.velocity)"
                      :src="getPingIcon(ip.velocity)"
                      alt=""
                      width="16"
                      height="16"
                      class="size-[16px]"
                  /></span>
                </td>
                <td class="px-[12px] py-[10px]">
                  <span class="inline-flex items-center gap-[6px]"
                    ><CommonImage
                      v-if="normalizeText(ip.flag)"
                      :src="normalizeText(ip.flag)"
                      alt=""
                      width="24"
                      height="16"
                      class="size-[24px] rounded-[2px] object-cover"
                    />{{ normalizeText(ip.countryName, '--') }}</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="getServerIps(selectedServer).updateDate" class="text-end text-[12px] text-[#86909c]" dir="ltr">
          {{ getServerIps(selectedServer).updateDate }}
        </div>
      </div>
    </ElDialog>

    <ElDialog v-model="isLeverageDialogOpen" :title="t('杠杆')" width="min(520px, calc(100vw - 32px))" append-to-body>
      <div v-if="selectedServer" class="max-h-[480px] overflow-auto">
        <div
          v-for="(level, index) in getLeverages(selectedServer)"
          :key="`${normalizeText(level.content)}-${index}`"
          class="border-b border-dashed border-[#e5e6eb] py-[12px] last:border-0"
        >
          <div class="mb-[10px] flex items-center gap-[8px] text-[16px] font-[600]">
            <span class="size-[12px] rounded-full bg-[#f4b615]"></span>{{ normalizeText(level.content, '--') }}
          </div>
          <div class="grid grid-cols-4 gap-[4px] text-center text-[12px] text-[#86909c]">
            <span>1:30</span><span>1:100</span><span>1:200</span><span>1:400</span>
          </div>
          <div class="mt-[6px] grid grid-cols-4 gap-[4px]">
            <span
              v-for="levelIndex in 4"
              :key="levelIndex"
              class="h-[6px] rounded-[3px]"
              :class="Number(level.leverageLevel) === levelIndex ? 'bg-[#f4b615]' : 'bg-[#e5e6eb]'"
            />
          </div>
        </div>
      </div>
    </ElDialog>

    <ElDialog
      v-model="isAppraiseDialogOpen"
      title="MT4/5 鉴定结果"
      width="min(520px, calc(100vw - 32px))"
      append-to-body
    >
      <div v-if="selectedLicense" class="flex items-center gap-[12px] rounded-[8px] bg-[#fafafa] p-[16px]">
        <CommonImage
          :src="normalizeText(selectedLicense.icon)"
          alt=""
          width="40"
          height="40"
          class="size-[40px] object-contain"
        />
        <div>
          <div class="text-[18px] font-[600]" :style="{ color: normalizeText(selectedLicense.color, '#1f845a') }">
            {{ normalizeText(selectedLicense.content, '--') }}
          </div>
          <div class="mt-[4px] text-[13px] text-[#7d818a]">{{ props.exchangeName || '' }}</div>
        </div>
      </div>
    </ElDialog>

    <ExchangeDetailExchangeSoftwareDialog
      v-model="isSoftwareDialogOpen"
      :items="modernSoftwareLists.all"
      :title="sectionTitle"
      :total-downloads-label="t('下载量')"
      @software-detail="openSoftwareDetail"
    />
    <CommonImagePreviewDialog v-model="previewVisible" :images="previewImages" :initial-index="previewInitialIndex" />
  </section>
</template>

<style scoped lang="less">
.screenshot-scrollbar {
  scrollbar-width: thin !important;
  scrollbar-color: #c5c8ce transparent !important;

  &::-webkit-scrollbar {
    display: block !important;
    width: 2px !important;
    height: 2px !important;
  }

  &::-webkit-scrollbar-track {
    background: transparent !important;
  }

  &::-webkit-scrollbar-thumb {
    min-height: 2px;
    border-radius: 999px;
    background: #c5c8ce !important;
  }
}

.exchange-software-module {
  :deep(.el-dialog__header) {
    padding-bottom: 16px;
  }

  :deep(.el-dialog__body) {
    padding-top: 0;
  }

  button:disabled {
    cursor: default;
    opacity: 0.8;
  }
}

@media (prefers-reduced-motion: reduce) {
  .exchange-software-module * {
    transition: none !important;
  }
}
</style>
